import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import Layout from '../components/layout/Layout';
import { useApp } from '../context/AppContext';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { toast } from 'sonner';
import { Gavel, Check, X, DollarSign } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const SellerAuctionRequestsPage = () => {
  const navigate = useNavigate();
  const { user, isAuthenticated } = useApp();
  const [auctions, setAuctions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [respondingTo, setRespondingTo] = useState(null);
  const [minPrice, setMinPrice] = useState('');
  const [responding, setResponding] = useState(false);

  const fetchPending = useCallback(async () => {
    if (!user?.id) return;
    try {
      const res = await fetch(`${API_URL}/api/auctions/seller/pending?user_id=${user.id}`);
      if (res.ok) setAuctions(await res.json());
    } catch (err) {
      console.error('Error:', err);
    } finally {
      setLoading(false);
    }
  }, [user?.id]);

  useEffect(() => {
    fetchPending();
  }, [fetchPending]);

  const handleRespond = async (auctionId, approved) => {
    setResponding(true);
    try {
      const body = { approved };
      if (approved && minPrice) body.minimum_price = parseFloat(minPrice);
      const res = await fetch(`${API_URL}/api/auctions/${auctionId}/seller-respond?user_id=${user.id}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body)
      });
      const data = await res.json();
      if (res.ok) {
        toast.success(approved ? 'Auction approved!' : 'Auction declined');
        setRespondingTo(null);
        fetchPending();
      } else {
        toast.error(data.detail || 'Failed to respond');
      }
    } catch (err) {
      toast.error('Error responding to auction request');
    } finally {
      setResponding(false);
    }
  };

  if (!isAuthenticated) {
    return (
      <Layout title="Auction Requests">
        <div className="flex flex-col items-center justify-center py-20 px-4">
          <Gavel className="w-16 h-16 text-gray-300 mb-4" />
          <h2 className="text-lg font-semibold mb-2">Sign in to view requests</h2>
          <Button onClick={() => navigate('/login')} className="bg-minted-600 hover:bg-minted-700">Sign In</Button>
        </div>
      </Layout>
    );
  }

  return (
    <Layout title="Auction Requests">
      <div className="bg-white px-4 py-4 border-b">
        <h2 className="font-bold text-lg text-gray-900 flex items-center gap-2">
          <Gavel className="w-5 h-5 text-amber-600" />
          Auction Requests
        </h2>
        <p className="text-sm text-gray-500">Items selected for auction — approve or decline</p>
      </div>

      {loading ? (
        <div className="flex justify-center py-16">
          <div className="w-8 h-8 border-3 border-amber-300 border-t-transparent rounded-full animate-spin" />
        </div>
      ) : auctions.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-16 px-4 text-center">
          <Gavel className="w-16 h-16 text-gray-300 mb-4" />
          <h3 className="font-semibold text-gray-900 mb-2">No pending requests</h3>
          <p className="text-gray-500 text-sm">When an admin selects your item for auction, it will appear here.</p>
        </div>
      ) : (
        <div className="p-4 space-y-4">
          {auctions.map(auction => (
            <div key={auction.id} className="bg-white rounded-xl shadow-sm border border-amber-100 overflow-hidden" data-testid={`auction-request-${auction.id}`}>
              <div className="flex">
                {auction.product_image && (
                  <img src={auction.product_image} alt="" className="w-24 h-24 object-cover" />
                )}
                <div className="flex-1 p-3">
                  <h3 className="font-medium text-gray-900 text-sm">{auction.product_title}</h3>
                  <p className="text-amber-600 font-bold mt-1">Min: ${auction.minimum_price?.toFixed(2)}</p>
                  <p className="text-[10px] text-gray-500 mt-1">
                    {new Date(auction.start_time).toLocaleDateString()} - {new Date(auction.end_time).toLocaleDateString()}
                  </p>
                </div>
              </div>
              <div className="flex border-t">
                <button
                  onClick={() => {
                    setRespondingTo(auction);
                    setMinPrice(auction.minimum_price?.toString() || '');
                  }}
                  className="flex-1 py-3 text-sm font-medium text-green-600 hover:bg-green-50 flex items-center justify-center gap-1"
                  data-testid={`approve-auction-${auction.id}`}
                >
                  <Check className="w-4 h-4" /> Approve
                </button>
                <button
                  onClick={() => handleRespond(auction.id, false)}
                  className="flex-1 py-3 text-sm font-medium text-red-600 hover:bg-red-50 flex items-center justify-center gap-1 border-l"
                  data-testid={`decline-auction-${auction.id}`}
                >
                  <X className="w-4 h-4" /> Decline
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Approve Dialog - let seller set minimum price */}
      <Dialog open={!!respondingTo} onOpenChange={() => setRespondingTo(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Approve Auction</DialogTitle>
          </DialogHeader>
          <div className="py-4 space-y-4">
            <p className="text-sm text-gray-600">
              Your item "{respondingTo?.product_title}" will be put up for auction. You can adjust the minimum price below.
            </p>
            <div>
              <Label>Minimum Bid Price ($)</Label>
              <div className="relative mt-1">
                <DollarSign className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                <Input
                  type="number"
                  value={minPrice}
                  onChange={(e) => setMinPrice(e.target.value)}
                  className="pl-8"
                  placeholder="Enter minimum price"
                  data-testid="seller-min-price-input"
                />
              </div>
              <p className="text-[10px] text-gray-400 mt-1">This is the lowest amount you'll accept</p>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setRespondingTo(null)}>Cancel</Button>
            <Button
              onClick={() => handleRespond(respondingTo.id, true)}
              disabled={responding}
              className="bg-green-600 hover:bg-green-700"
              data-testid="confirm-approve-auction-btn"
            >
              {responding ? 'Approving...' : 'Approve Auction'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Layout>
  );
};

export default SellerAuctionRequestsPage;
